<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("issue_report.list.title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button
                        v-if="isAdmin"
                        size="sm"
                        type="neutral"
                        :loading="syncing"
                        @click="runSync"
                    >
                        <i class="fa fa-sync" v-if="!syncing"></i>
                        {{ $t("issue_report.list.sync_now") }}
                    </base-button>
                    <base-button size="sm" type="neutral" @click="goCreate">
                        <i class="fa fa-plus"></i>
                        {{ $t("issue_report.list.report_button") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col-12 col-md-6">
                            <h3 class="mb-0">{{ $t("issue_report.list.title") }}</h3>
                        </div>
                        <div class="col-12 col-md-6 text-md-right mt-2 mt-md-0" v-if="isAdmin">
                            <el-radio-group v-model="scope" size="small" @change="onScopeChange">
                                <el-radio-button label="mine">
                                    {{ $t("issue_report.list.scope_mine") }}
                                </el-radio-button>
                                <el-radio-button label="all">
                                    {{ $t("issue_report.list.scope_all") }}
                                </el-radio-button>
                            </el-radio-group>
                        </div>
                    </div>
                </template>

                <!-- ============ Table ============ -->
                <div v-loading="loading">
                    <el-table
                        :data="rows"
                        row-key="id"
                        header-row-class-name="thead-light"
                        :empty-text="$t('issue_report.list.no_data')"
                    >
                        <el-table-column :label="$t('issue_report.list.col_created_at')" min-width="150px">
                            <template slot-scope="{ row }">
                                {{ formatTime(row.created_at) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('issue_report.list.col_title')" min-width="220px">
                            <template slot-scope="{ row }">
                                <el-tag :type="kindTagType(row.kind)" size="mini" class="mr-2">
                                    <i :class="kindIcon(row.kind)"></i>
                                    {{ kindLabel(row.kind) }}
                                </el-tag>
                                <span>{{ row.title }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column
                            v-if="scope === 'all'"
                            :label="$t('issue_report.list.col_reporter')"
                            min-width="130px"
                        >
                            <template slot-scope="{ row }">
                                {{ row.reporter_name || "-" }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('issue_report.list.col_component')" min-width="100px">
                            <template slot-scope="{ row }">
                                {{ componentLabel(row.component) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('issue_report.list.col_severity')" min-width="90px">
                            <template slot-scope="{ row }">
                                <span v-if="row.severity">{{ severityLabel(row.severity) }}</span>
                                <span v-else>-</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('issue_report.list.col_work_status')" min-width="150px">
                            <template slot-scope="{ row }">
                                <el-tag :type="workStatusTagType(row.work_status)" size="small">
                                    {{ workStatusLabel(row.work_status) }}
                                </el-tag>
                                <div
                                    v-if="row.work_status === 'done' && row.resolved_version"
                                    class="text-muted small mt-1"
                                >
                                    {{ $t("issue_report.list.resolved_in", { version: row.resolved_version }) }}
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('issue_report.list.col_attachments')" min-width="90px" align="center">
                            <template slot-scope="{ row }">
                                <el-popover
                                    v-if="attachmentsOf(row).length"
                                    placement="left"
                                    trigger="click"
                                    width="260"
                                >
                                    <div class="issue-attach-pop">
                                        <a
                                            v-for="(url, i) in attachmentsOf(row)"
                                            :key="i"
                                            :href="url"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <img :src="url" class="issue-attach-thumb" />
                                        </a>
                                    </div>
                                    <a slot="reference" href="javascript:void(0)" class="issue-attach-trigger">
                                        <i class="fa fa-image"></i>
                                        <span class="ml-1">{{ attachmentsOf(row).length }}</span>
                                    </a>
                                </el-popover>
                                <span v-else>-</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('issue_report.list.col_github')" min-width="90px" align="center">
                            <template slot-scope="{ row }">
                                <a
                                    v-if="row.gh_url"
                                    :href="row.gh_url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    :title="row.gh_issue_number ? '#' + row.gh_issue_number : 'GitHub'"
                                >
                                    <i class="fab fa-github" style="font-size: 18px;"></i>
                                    <span v-if="row.gh_issue_number" class="ml-1">#{{ row.gh_issue_number }}</span>
                                </a>
                                <span v-else>-</span>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <!-- ============ Pagination ============ -->
                <div class="px-4 py-3 d-flex justify-content-end align-items-center">
                    <span class="text-muted mr-3">{{ $t("issue_report.list.total_count") }}: {{ total }}</span>
                    <el-pagination
                        background
                        layout="sizes, prev, pager, next"
                        :total="total"
                        :page-size="per_page"
                        :page-sizes="[20, 50, 100]"
                        :current-page.sync="page"
                        @size-change="onSizeChange"
                        @current-change="onPageChange"
                    ></el-pagination>
                </div>
            </card>
        </div>
    </div>
</template>

<script>
import {
    Table,
    TableColumn,
    Tag,
    Pagination,
    RadioGroup,
    RadioButton,
    Popover
} from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { mapActions, mapGetters } from "vuex";
import swal from "sweetalert2";
import moment from "moment";

export default {
    name: "IssueReports",
    components: {
        RouteBreadCrumb,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Pagination.name]: Pagination,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        [Popover.name]: Popover
    },
    data() {
        return {
            loading: false,
            syncing: false,
            scope: "mine",
            rows: [],
            total: 0,
            page: 1,
            per_page: 20
        };
    },
    computed: {
        ...mapGetters({
            isAdmin: "Auth/isAdmin"
        })
    },
    mounted() {
        this.loadData();
    },
    methods: {
        ...mapActions({
            ListIssueReports: "IssueReport/List",
            SyncIssueReports: "IssueReport/Sync"
        }),
        queryParams() {
            const p = { page: this.page, per_page: this.per_page };
            p.mine = this.scope === "all" && this.isAdmin ? "all" : 1;
            return p;
        },
        loadData() {
            this.loading = true;
            this.ListIssueReports(this.queryParams())
                .then(res => {
                    const data = (res && res.data) || {};
                    this.rows = data.items || [];
                    this.total = data.total || 0;
                })
                .catch(err => {
                    console.log("list issue reports error", err);
                    swal.fire({
                        icon: "error",
                        title: this.$t("issue_report.list.load_error"),
                        text: "",
                        confirmButtonClass: "btn btn-danger btn-fill",
                        buttonsStyling: false
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        runSync() {
            this.syncing = true;
            this.SyncIssueReports()
                .then(res => {
                    const data = (res && res.data) || {};
                    swal.fire({
                        icon: "success",
                        title: this.$t("issue_report.list.sync_success", {
                            created: data.forward_created || 0,
                            touched: data.reverse_touched || 0
                        }),
                        timer: 2500,
                        showConfirmButton: false
                    });
                    // Refresh the list so newly-synced GitHub numbers / statuses show.
                    this.loadData();
                })
                .catch(err => {
                    console.log("sync issue reports error", err);
                    swal.fire({
                        icon: "error",
                        title: this.$t("issue_report.list.sync_error"),
                        text: "",
                        confirmButtonClass: "btn btn-danger btn-fill",
                        buttonsStyling: false
                    });
                })
                .finally(() => {
                    this.syncing = false;
                });
        },
        onScopeChange() {
            this.page = 1;
            this.loadData();
        },
        onPageChange(p) {
            this.page = p;
            this.loadData();
        },
        onSizeChange(size) {
            this.per_page = size;
            this.page = 1;
            this.loadData();
        },
        goCreate() {
            this.$router.push("/admin/issue-reports/new");
        },
        // Normalize attachments across API shapes: prefer the array
        // (attachment_urls), fall back to the legacy single attachment_url.
        attachmentsOf(row) {
            if (Array.isArray(row.attachment_urls) && row.attachment_urls.length) {
                return row.attachment_urls.filter(Boolean);
            }
            return row.attachment_url ? [row.attachment_url] : [];
        },
        formatTime(v) {
            if (!v) return "-";
            const m = moment(v);
            return m.isValid() ? m.format("YYYY-MM-DD HH:mm") : "-";
        },
        componentLabel(c) {
            const key = "issue_report.component." + c;
            const label = this.$t(key);
            return label === key ? c : label;
        },
        severityLabel(s) {
            const key = "issue_report.severity." + s;
            const label = this.$t(key);
            return label === key ? s : label;
        },
        kindLabel(k) {
            const key = "issue_report.kind." + k;
            const label = this.$t(key);
            return label === key ? k : label;
        },
        kindIcon(k) {
            return k === "feature" ? "fa fa-lightbulb" : "fa fa-bug";
        },
        kindTagType(k) {
            return k === "feature" ? "success" : "danger";
        },
        workStatusLabel(s) {
            const key = "issue_report.work_status." + s;
            const label = this.$t(key);
            return label === key ? s : label;
        },
        workStatusTagType(s) {
            switch (s) {
                case "pending_review":
                    return "info";
                case "in_progress":
                    return "warning";
                case "done":
                    return "success";
                case "cancelled":
                    return "danger";
                default:
                    return "info";
            }
        }
    }
};
</script>

<style scoped>
.issue-attach-trigger {
    color: #5e72e4;
    cursor: pointer;
    white-space: nowrap;
}
.issue-attach-pop {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.issue-attach-thumb {
    width: 72px;
    height: 72px;
    object-fit: cover;
    border: 1px solid #e9ecef;
    border-radius: 4px;
}
</style>
